import { getEntitySubdomain } from '@opengov/app-core/entity';
import { IReportConfiguration } from '@opengov/reporting-types';
import { BasicColumnTypes } from '@opengov/ogql-types';
import { composeEffects } from '@opengov/compose-app/core/module';
import { createStructuredSelector } from '@opengov/compose-app/reselect';
import { selectUseQueryService } from '@opengov/ogql-client/selectors';

import { reportDimensionalEffects } from '../../../reportExplorer/effects/reportDimensionalEffects';
import { selectReportConfigWithMetadata } from '../../../reportExplorer/selectors/selectReportExplorerState';
import { summaryTableActionCreators } from '../../../summaryTable/state';
import { selectUseLargeScaleQueryLimit } from '../../flags';
import { reportLocaleText } from '../../locale';
import {
  selectCurrentReport,
  selectIsReportDimensional,
  selectIsReportMultiDataset
} from '../../report/selectors/reportSelectors';
import { selectCurrentLens, selectCurrentTemplate } from '../../reportTemplate/standardTemplateSelectors';
import { selectIsInStandardReport } from '../selectors';

import { detailTableSumQuery } from './queries/detailTableQuery';
import { visualizationQuery } from './queries/visualizationQuery';

function doDetailTableSumQuery(reportConfig: IReportConfiguration): boolean {
  if (reportConfig.detailTableQuery.showTotals)
    return (
      reportConfig.detailTableQuery.columns.filter((col) =>
        [BasicColumnTypes.Currency, BasicColumnTypes.Numeric, BasicColumnTypes.Percent].includes(col.type)
      ).length > 0
    );
  return false;
}

export const getAndRunAllQueries = async ({
  isDimensional,
  isMultiDataset = false,
  isStandardTemplate,
  lens,
  reportMsgs,
  template,
  useLargeScaleQueryLimit,
  useQueryService,
  updatedReportConfig,
  report,
  entitySubdomain: _entitySubdomain
}) => {
  if (isMultiDataset) {
    await reportDimensionalEffects.getColumnData();
    return;
  }

  if (!updatedReportConfig) {
    return;
  }

  // this means just the CoA summary table
  if (isDimensional) {
    return reportDimensionalEffects.getDimensionalGroupData(updatedReportConfig);
  }

  // We need to clear the summary table state to avoid showing incorrect
  // totals in the case there ends up being too much data to display
  void summaryTableActionCreators.resetState();

  const queryId = report?.lensId ?? report?.viewId ?? '';
  const payload = {
    lensId: lens?.id,
    queryId,
    templateId: template?.id || null,
    reportConfiguration: updatedReportConfig,
    reportMsgs,
    useQueryService,
    isStandardTemplate
  };

  const queries = [
    visualizationQuery({
      ...payload,
      useLargeScaleQueryLimit
    })
  ];

  if (doDetailTableSumQuery(updatedReportConfig)) {
    queries.push(detailTableSumQuery(payload));
  }

  return Promise.all(queries);
};

export const reportQueryEffects = composeEffects('report-query', (compose) => ({
  query: compose.statefulEffect(
    createStructuredSelector({
      isDimensional: selectIsReportDimensional,
      isMultiDataset: selectIsReportMultiDataset,
      isStandardTemplate: selectIsInStandardReport,
      lens: selectCurrentLens,
      reportMsgs: reportLocaleText,
      template: selectCurrentTemplate,
      useLargeScaleQueryLimit: selectUseLargeScaleQueryLimit,
      useQueryService: selectUseQueryService,
      updatedReportConfig: selectReportConfigWithMetadata,
      report: selectCurrentReport,
      entitySubdomain: getEntitySubdomain
    }),
    getAndRunAllQueries
  )
}));
